/**
 * Migration from PoseBinaryFormat v1/v2 → CanonicalPersistenceFormat v3.
 *
 * Reads a legacy-format byte buffer, decodes via the existing pose
 * binary decoder, materialises an in-memory PoseData, then routes it
 * through the legacy-pose-data adapter to produce CanonicalFrames.
 *
 * docs/architecture-spec.md §9.
 */

import { decodePoseBinary } from '../modules/PoseBinaryFormat';
import { buildPoseDataFromBinary } from '../viewer/PoseData';
import { legacyPoseDataToCanonical } from '../adapters/legacy-pose-data';
import { makeCanonicalRecording, type CanonicalRecording } from './CanonicalPersistenceFormat';

export interface MigrateV2Opts {
  streamId: string;
  deviceIdHash: string;
  /** Optional pre-set subjectId; the migrated recording carries no
   *  SubjectProfile. Hosts add it post-migration. */
  sessionId?: string | null;
  /** 3x3 row-major rotation matrix from GravityAnchor.
   *  buildGravityRotation(). When provided, MediaPipe-derived
   *  pos3DLocal is rotated to a gravity-aligned world frame. */
  gravityRotation?: readonly number[] | null;
}

export interface MigrateV2Result extends CanonicalRecording {
  /** True when the legacy adapter actually applied a gravity rotation
   *  (matrix supplied AND not the identity). */
  gravityApplied: boolean;
}

export async function migrateLegacyPoseBinaryToCanonical(
  legacyBytes: Uint8Array,
  opts: MigrateV2Opts,
): Promise<MigrateV2Result> {
  const decoded = await decodePoseBinary(legacyBytes);
  const data = buildPoseDataFromBinary(decoded);
  const { header, frames, gravityApplied } = legacyPoseDataToCanonical(data, {
    streamId: opts.streamId,
    deviceIdHash: opts.deviceIdHash,
    gravityRotation: opts.gravityRotation,
  });
  const recording = makeCanonicalRecording(header, frames, null, opts.sessionId ?? null);
  return { ...recording, gravityApplied };
}
